import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
// Legal Content Constants
const TERMS_CONTENT = `
  <h3 class="text-xl font-bold mb-4 text-[#1a4d3e]">Términos y Condiciones</h3>
  <p class="mb-4">Bienvenido a Metabo-Reset 40+. Al acceder a nuestro sitio web y utilizar nuestros servicios, aceptas cumplir con los siguientes términos y condiciones:</p>
  
  <h4 class="font-bold mb-2 text-[#1a4d3e]">1. Uso del Servicio</h4>
  <p class="mb-4">El contenido de este programa es únicamente para fines informativos y educativos. No sustituye el consejo médico profesional, diagnóstico o tratamiento.</p>
  
  <h4 class="font-bold mb-2 text-[#1a4d3e]">2. Propiedad Intelectual</h4>
  <p class="mb-4">Todo el contenido, incluyendo textos, gráficos, logos e imágenes, es propiedad exclusiva de Metabo-Reset 40+ y está protegido por leyes de derechos de autor.</p>
  
  <h4 class="font-bold mb-2 text-[#1a4d3e]">3. Modificaciones</h4>
  <p class="mb-4">Nos reservamos el derecho de modificar estos términos en cualquier momento. Los cambios entrarán en vigor inmediatamente después de su publicación en el sitio web.</p>
`;
const PRIVACY_CONTENT = `
  <h3 class="text-xl font-bold mb-4 text-[#1a4d3e]">Política de Privacidad</h3>
  <p class="mb-4">En Metabo-Reset 40+, valoramos y respetamos tu privacidad. Esta política describe cómo recopilamos, usamos y protegemos tu información personal:</p>
  
  <h4 class="font-bold mb-2 text-[#1a4d3e]">1. Información que Recopilamos</h4>
  <p class="mb-4">Podemos recopilar información personal como tu nombre, dirección de correo electrónico y detalles de pago cuando te registras en nuestro programa.</p>
  
  <h4 class="font-bold mb-2 text-[#1a4d3e]">2. Uso de la Información</h4>
  <p class="mb-4">Utilizamos tu información para procesar transacciones, enviar actualizaciones del programa y mejorar nuestros servicios. No vendemos tu información a terceros.</p>
  
  <h4 class="font-bold mb-2 text-[#1a4d3e]">3. Seguridad de Datos</h4>
  <p class="mb-4">Implementamos medidas de seguridad robustas para proteger tu información personal contra acceso no autorizado, alteración o divulgación.</p>
  
  <h4 class="font-bold mb-2 text-[#1a4d3e]">4. Cookies</h4>
  <p class="mb-4">Utilizamos cookies para mejorar tu experiencia en nuestro sitio web. Puedes configurar tu navegador para rechazar las cookies si lo prefieres.</p>
`;
type ModalType = 'terms' | 'privacy' | null;
interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
}
function LegalModal({
  isOpen,
  onClose,
  title,
  content
}: LegalModalProps) {
  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  return <AnimatePresence data-id="element-62">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-id="element-63">
            {/* Modal Content */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col relative" data-id="element-64">
              {/* Header */}
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-[#faf8f5]" data-id="element-65">
                <h3 className="text-xl font-serif font-bold text-[#1a4d3e]" data-id="element-66">
                  {title}
                </h3>
                <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500 hover:text-[#1a4d3e]" aria-label="Cerrar" data-id="element-67">
                  <X size={20} data-id="element-68" />
                </button>
              </div>

              {/* Scrollable Body */}
              <div className="p-6 overflow-y-auto custom-scrollbar text-gray-600 leading-relaxed" data-id="element-69">
                <div dangerouslySetInnerHTML={{
              __html: content
            }} data-id="element-70" />
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end" data-id="element-71">
                <button onClick={onClose} className="px-6 py-2 bg-[#1a4d3e] text-white rounded-full font-semibold text-sm hover:bg-[#143d31] transition-colors" data-id="element-72">
                  Cerrar
                </button>
              </div>
            </motion.div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-[#1a4d3e] text-white py-12 border-t border-white/10" data-id="element-73">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-74">
          <div className="flex flex-col md:flex-row justify-between items-center" data-id="element-75">
            <div className="mb-6 md:mb-0 text-center md:text-left" data-id="element-76">
              <span className="text-xl font-serif font-bold block" data-id="element-77">
                Metabo-Reset 40+
              </span>
              <p className="text-sm text-gray-400 mt-2 max-w-xs" data-id="element-78">
                Recupera tu cuerpo, recupera tu vida. El protocolo definitivo
                para mujeres de 40+.
              </p>
            </div>

            <div className="flex gap-8 text-sm text-gray-300 font-medium" data-id="element-79">
              <button onClick={() => setActiveModal('terms')} className="hover:text-[#c9a227] transition-colors relative group" data-id="element-80">
                Términos
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#c9a227] transition-all group-hover:w-full" data-id="element-81"></span>
              </button>
              <button onClick={() => setActiveModal('privacy')} className="hover:text-[#c9a227] transition-colors relative group" data-id="element-82">
                Privacidad
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#c9a227] transition-all group-hover:w-full" data-id="element-83"></span>
              </button>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-white/10 text-center text-xs text-gray-500 flex flex-col sm:flex-row justify-between items-center gap-4" data-id="element-84">
            <span data-id="element-85">
              &copy; {new Date().getFullYear()} Protocolo Metabo-Reset 40+.
              Todos los derechos reservados.
            </span>
            <span className="text-gray-600" data-id="element-86">
              Diseñado con ❤️ para tu bienestar
            </span>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <LegalModal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" content={TERMS_CONTENT} data-id="element-87" />

      <LegalModal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" content={PRIVACY_CONTENT} data-id="element-88" />
    </>;
}